/* eslint-disable max-len */
import dayjs from 'dayjs';
import momentJS from 'moment';
import 'moment/locale/fr';

momentJS.locale('fr');

const DAYS = {
    0: 'Lundi',
    1: 'Mardi',
    2: 'Mercredi',
    3: 'Jeudi',
    4: 'Vendredi',
    5: 'Samedi',
    6: 'Dimanche',
};
const DAYS_ABB = {
    0: 'Lun',
    1: 'Mar',
    2: 'Mer',
    3: 'Jeu',
    4: 'Ven',
    5: 'Sam',
    6: 'Dim',
};
const DAYS_IN_FRENCH = {
    sun: 'Lundi',
    mon: 'Mardi',
    tue: 'Mercredi',
    wed: 'Jeudi',
    thu: 'Vendredi',
    fri: 'Samedi',
    sat: 'Dimanche',
};
const DAYS_IN_FRENCH_ABB = {
    sun: 'Lun',
    mon: 'Mar',
    tue: 'Mer',
    wed: 'Jeu',
    thu: 'Ven',
    fri: 'Sam',
    sat: 'Dim',
};
const dateUtils = {
    DAYS,
    DAYS_IN_FRENCH,
    DAYS_IN_FRENCH_ABB,
    DAYS_ABB,
    DAY_IN_MILIS: 24 * 60 * 60 * 1000,
    HOUR_IN_MILIS: 60 * 60 * 1000,
    humanizeTimeDuration: (date: Date) => {
        return momentJS(date).fromNow();
    },
    getDuration: (date: Date) => {
        const now = momentJS();
        const duration = momentJS.duration(now.diff(date));
        const minutes = duration.asMinutes();
        const hours = duration.asHours();
        const days = duration.asDays();
        const weeks = duration.asWeeks();
        if (minutes < 60) {
            return `${Math.floor(minutes)}m`;
        }
        if (hours < 24) {
            return `${Math.floor(hours)}h`;
        }
        if (days < 7) {
            return `${Math.floor(days)}d`;
        }
        return `${Math.floor(weeks)}w`;
    },
    getDate: (stringDate = '') => (stringDate !== '' ? dayjs(stringDate) : dayjs()),
    getDiffDate: (firstDate, secondDate) => {
        let totalDiffInDay = firstDate.diff(secondDate, 'day');
        const totalDiffInHour = firstDate.diff(secondDate, 'hours');
        const totalDiffInMinute = firstDate.diff(secondDate, 'minutes');
        const totalDiffInSecond = firstDate.diff(secondDate, 'seconds');
        const hour = dateUtils.prependZero(totalDiffInHour - totalDiffInDay * 24);
        const minute = dateUtils.prependZero(totalDiffInMinute - totalDiffInHour * 60);
        const second = dateUtils.prependZero(totalDiffInSecond - totalDiffInMinute * 60);
        totalDiffInDay = dateUtils.prependZero(totalDiffInDay);

        return {
            day: totalDiffInDay,
            hour,
            minute,
            second,
        };
    },
    // eslint-disable-next-line radix
    getDiffDateInSecond: (firstDate, secondDate) => parseInt(firstDate.diff(secondDate)) / 1000,
    convertDateTimeObjToSecond: dateTimeObj => {
        const { day, hour, minute, second } = dateTimeObj;
        const dayInSecond = day * 86400;
        const hourInSecond = hour * 3600;
        const minuteInSecond = minute * 60;
        return dayInSecond + hourInSecond + minuteInSecond + second;
    },
    getNextNMinutesInMs: minute => dayjs().valueOf() + minute * 60000,
    isSameDateTime: (firstDate, secondDate) => dayjs(firstDate).valueOf() === dayjs(secondDate).valueOf(),
    isExpired: expiry => {
        const nowDate = dayjs();
        const expiryDate = dayjs(expiry);
        const dateDiff = expiryDate.diff(nowDate);
        return dateDiff <= dateUtils.HOUR_IN_MILIS;
    },
    getExperienceDiff: date => {
        const month = new Date(date).getMonth();
        const year = new Date(date).getFullYear();
        const yearDiff = new Date().getFullYear() - year;
        const monthDiff = Math.abs(new Date().getMonth() - month);
        let result = '0';
        if (yearDiff > 0) {
            if (yearDiff === 1) {
                result = `${yearDiff} an +`;
            } else {
                result = `${yearDiff} ans +`;
            }
        } else {
            result = `${monthDiff} mois +`;
        }

        return result;
    },
    durationMonthYearByMonths: (numberOfMonths) => {
        const years = Math.floor(numberOfMonths / 12);
        const months = Math.round(numberOfMonths) % 12;
        let result = '';
        if (years > 0) {
            if (years === 1) {
                result = `${years} an`;
            } else {
                result = `${years} ans`;
            }
            if (months > 0) {
                result += ` ${months} mois`;
            }
        } else {
            result = `${months} mois`;
        }

        return result;
    },
    durationMonthYear: (startDate, endDate) => {
        const start = moment(new Date(startDate));
        const end = moment(new Date(endDate ?? Date.now()));

        const duration = momentJS.duration(end.diff(start));

        let years = duration.years();
        let months = duration.months();

        /**
         * If the duration is 12 months, we convert it to 1 year
         */
        if (months === 12) {
            years = 1;
            months = 0;
        }

        /**
         * if the month is the same or if the duration is more than 1 day add one month
         */
        let days = duration.days();
        if (days > 0 || start.month() === end.month()) {
            months += 1;
        }

        let result = '';
        if (years > 0) {
            if (years === 1) {
                result = `${years} an`;
            } else {
                result = `${years} ans`;
            }
            if (months > 0) {
                result += ` ${months} mois`;
            }
        } else {
            result = `${months} mois`;
        }

        return result;
    },
    isNotExpired: expiry => dayjs(expiry).valueOf() > dayjs().valueOf(),
    prependZero: number => {
        let prependedNumber = number;
        const numberLength = number.toString().length;
        if (number < 10) {
            if (numberLength === 2) {
                prependedNumber = `- 0${Math.abs(number)} `;
            } else if (numberLength === 1) {
                prependedNumber = `0${Math.abs(number)} `;
            }
        }
        return prependedNumber;
    },
    convertToReadableDatetime: datetime => dayjs(datetime).format('MMM DD, YYYY, HH:mm a'),
    convertToReadableDayMonth: datetime => dayjs(datetime).format('MMM DD'),
    convertToReadableDate: datetime => dayjs(datetime).format('MMM DD YYYY'),
    minuteToHour: baseMinute => {
        let hours = Math.floor(baseMinute / 60);
        let minutes = baseMinute % 60;
        return `${hours}h ${minutes} m`;
    },
    hourMinuteStringToMinute: hourMinuteString => {
        let split = hourMinuteString.split(':');
        let hour = parseInt(split[0]);
        let hourToMinute = hour * 60;
        let minute = parseInt(split[1]);
        return hourToMinute + minute;
    },
    getDaysArrayByMonth: (month = momentJS().format('MMMM')) => {
        let arrDays = [];
        var start = momentJS(`${momentJS().format('YYYY')} -${month} `, 'YYYY-MMMM');
        for (
            var end = momentJS(start).add(1, 'month');
            start.isBefore(end);
            start.add(1, 'day')
        ) {

            arrDays.push(momentJS(start));
        }

        return arrDays;
    },
    convertHourMinuteToString: (hour, minute) => {
        return `${hour < 10 ? '0' : ''}${hour}:${minute < 10 ? '0' : ''}${minute} `;
    },
    convertToDatePickerValue: hourMinute => {
        const hourMinuteSplitted = hourMinute.split(':');
        const hour = hourMinuteSplitted.length > 1 ? parseInt(hourMinuteSplitted[0]) : 0;
        const minute = hourMinuteSplitted.length > 1 ? parseInt(hourMinuteSplitted[1]) : 0;
        return new Date(new Date().setHours(hour, minute, 0, 0));
    },
    diffHourInStringToNumber: (start, end) => {
        const startTotalMinute = dateUtils.hourMinuteStringToMinute(start);
        const endTotalMinute = dateUtils.hourMinuteStringToMinute(end);
        const totalMinute = endTotalMinute - startTotalMinute;
        return totalMinute;
    },
    getDayHourMinuteByPlan: plan => {
        let totalMinute = 0;
        let totalMinuteBreak = 0;
        let days = [];
        for (const key in plan) {
            const element = plan[key];
            if (!key.includes('_break')) {
                if (element?.start && element?.end) {
                    days.push(dateUtils.DAYS_IN_FRENCH_ABB[key]);
                }
                if (element?.start && element?.end) {
                    const diffMinute = dateUtils.diffHourInStringToNumber(element.start, element.end);
                    let diffMinuteBreak = 0;
                    const elementBreak = plan[key + '_break'];
                    if (elementBreak) {
                        diffMinuteBreak = dateUtils.diffHourInStringToNumber(elementBreak.start, elementBreak.end);
                    }
                    totalMinuteBreak += diffMinuteBreak;
                    totalMinute += diffMinute;
                }
            }
        }
        const hour = (totalMinute - totalMinuteBreak) / 60;
        const minute = (totalMinute - totalMinuteBreak) % 60;

        return { days, hour, minute };
    },
    getTitlePlan: plan => {
        const { days, hour, minute } = plan;
        return {
            hour,
            description: `${dateUtils.prependZero(hour)}:${dateUtils.prependZero(minute)} jours\n${days.join(', ')} `,
        };
    },
    getToday: () => momentJS(new Date()).format('DD/MM/YYYY HH:mm:ss'),
    getTotalDays: plan => {
        const { days } = dateUtils.getDayHourMinuteByPlan(plan);
        return {
            total: days.length,
            description: `${days.length} jours\n${days.join(', ')} `,
        };
    },
    formatDate: (date: string, format = 'DD/MM/YYYY') => momentJS(date).format(format),
    getYear: (date: string) => moment(date).format('YYYY'),
};

export const moment = momentJS;

export default dateUtils;
